import * as Yup from "yup";

import {
  CreateOrUpdateAdjustmentStatment,
  CreateOrUpdateAdjustmentStatmentVariables,
} from "_graphql/mutation/__generated__/CreateOrUpdateAdjustmentStatment";

import { AdjustmentStatementForm } from "components/forms";
import { LocationGenerics } from "router/location";
import { Modal } from "components";
import _ from "lodash";
import { createOrUpdateAdjustmentStatment } from "_graphql/visal/mutations/treaty";
import toast from "react-hot-toast";
import { useAppSearch } from "_graphql/cache/auth";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useMatch } from "react-location";
import { useMutation } from "@apollo/client";
import { useTreaty } from "hooks/data/use-treaties";
import { wrapClick } from "utils";

const validationSchema = Yup.object().shape({
  claim_paid: Yup.number()
    .min(0, "Number cannot be less than 0")
    .required("Required"),
  outstanding_payment: Yup.number()
    .min(0, "Number cannot be less than 0")
    .required("Required"),
  retained_premium_income: Yup.number()
    .min(0, "Number cannot be less than 0")
    .required("Required"),
  //   treatyNpDetailId: Yup.string().required("Required"),
});

export default function CreateAdjustmentStatementContainer({
  open,
  setOpen,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const searchParams = useAppSearch();
  const { params } = useMatch<LocationGenerics>();
  const { loading: fetchingTreaty, treaty } = useTreaty({
    treatyId: params?.treaty,
  });
  const isEdit = searchParams?.modal === "update-adjustment-statement";
  const [create, { loading }] = useMutation<
    CreateOrUpdateAdjustmentStatment,
    CreateOrUpdateAdjustmentStatmentVariables
  >(createOrUpdateAdjustmentStatment, {
    refetchQueries: ["Treaty"],
  });

  const form = useFormik<CreateOrUpdateAdjustmentStatmentVariables>({
    initialValues: {
      treaty_id: params?.treaty || "",
      claim_paid: undefined,
      retained_premium_income: undefined,
      treaty_np_detail_id: treaty?.treaty_np_detail?.treaty_np_id,
    },
    validationSchema: validationSchema,
    onSubmit: async (variables) => {
      await create({
        variables: {
          ...variables,
          treaty_np_detail_id: treaty?.treaty_np_detail?.treaty_np_id,
        },
      }).then(({ data }) => {
        if (data?.createOrUpdateAdjustmentStatment) {
          toast.success(
            `Adjustment Statement ${
              treaty?.treaty_np_detail?.claims_paid ? "updated" : "created"
            } successfully`
          );
          refetch?.();
          setOpen?.(false);
          form.resetForm();
        } else {
          toast.error(
            `Failed to ${
              treaty?.treaty_np_detail?.claims_paid ? "update" : "create"
            } Adjustment Statement`
          );
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  useEffect(() => {
    if (treaty) {
      form.setValues({
        ...form.values,
        treaty_np_detail_id: treaty?.treaty_np_detail?.treaty_np_id,
        claim_paid: _.get(treaty, "treaty_np_detail.claims_paid", null),
        outstanding_payment: _.get(
          treaty,
          "treaty_np_detail.outstanding_payment",
          null
        ),
        retained_premium_income: _.get(treaty, "treaty_np_detail.egrnpi", null),
      });
    }
  }, [treaty]);

  return (
    <Modal
      open={open}
      loading={fetchingTreaty}
      setOpen={setOpen}
      title={`${isEdit ? "Update" : "Create"} Adjustment Statement`}
      description="Provide the details for the adjustment statement"
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={loading}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading
              ? `${isEdit ? "Updating" : "Creating"} ...`
              : isEdit
                ? "Update Adjustment statement"
                : "Create Adjustment Statement"}
          </button>
        </>
      )}
    >
      {/* {JSON.stringify(treaty?.treaty_np_detail, null, 2)} */}
      <AdjustmentStatementForm form={form} />
    </Modal>
  );
}
